/**
 * Copyright (c) 2023 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import React from 'react';
import { useDispatch } from 'react-redux';

import { colors } from '@blend/consumer-components';

import { addNotifications } from '../../../actions/pageActions';
import { notaryAPI } from '../../../api/notary';
import { NotificationItem } from '../../common/Notifications/NotificationItem';
import SigningDocument from '../../common/Documents/SigningDocument';
import { ConfirmationModal } from '../../common/Modals/ConfirmationModal/ConfirmationModal';
import { useRoom } from '../../../hooks/useRoom';
import { DocumentStatus } from '@shared/constants/document';

export namespace RemoveEnoteConfirmationModal {
  export interface Props {
    isModalOpen: boolean;
    handleModalClose: () => void;
    document: SigningDocument;
  }
}

export const RemoveEnoteConfirmationModal: React.FC<RemoveEnoteConfirmationModal.Props> = ({
  isModalOpen,
  handleModalClose,
  document,
}) => {
  const dispatch = useDispatch();
  const { roomDocumentId, status } = document;
  const { roomId } = useRoom();
  const isEnoteSigned = [DocumentStatus.SIGNED, DocumentStatus.COMPLETED].includes(status);
  const removeEnoteHeading =
    'Do you want to remove the eNote from the signing package? You will not be able to restore it.';
  const cannotRemoveEnoteHeading = 'You cannot remove this eNote because it has been signed by all participants.';

  const removeEnote = async () => {
    handleModalClose();
    if (isEnoteSigned) {
      return;
    }
    try {
      await notaryAPI.documents.discardDocument(roomId, roomDocumentId!);
    } catch (err) {
      const notifications: NotificationItem.PropsWithoutId[] = [
        {
          type: 'error',
          iconType: 'error',
          content: (err as Error).message,
          shouldAutoRemove: true,
        },
      ];
      addNotifications(notifications)(dispatch);
    }
  };

  return (
    <ConfirmationModal
      isOpen={isModalOpen}
      contentLabel={'Remove eNote'}
      heading={isEnoteSigned ? cannotRemoveEnoteHeading : removeEnoteHeading}
      confirmButtonText={isEnoteSigned ? 'Ok' : 'Yes'}
      handleConfirm={removeEnote}
      handleClose={isEnoteSigned ? undefined : handleModalClose}
      closeButtonText={'Cancel'}
      buttonColor={colors.reflex}
      data-it="RemoveEnoteConfirmationModal"
    />
  );
};
